import { isApiError } from "@sagot/api-client";
import { PRODUCT_NAME } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, ErrorState, Icon, cn, toast } from "@sagot/ui";
import { useState } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import { usePermissions } from "../../../../session/permissions";
import { useClient } from "../../../../session/session-provider";
import { useResetOnPageShow, useZohoMutations, useZohoStatus } from "../use-zoho";
import { chosenDatacenter, hasRegionChoice, isLinked, isZohoAuthorizeUrl, type ZohoStatus } from "../zoho-model";
import { useCanManage } from "./panel-kit";
import { ZOHO_I18N, ZohoCardSkeleton, ZohoDetails, ZohoHeader, ZohoRegionSelect } from "./zoho-card-parts";
import { ZohoDisconnectDialog } from "./zoho-disconnect-dialog";

const SERVICE_UNAVAILABLE = 503;

type Redirect = (url: string) => void;

interface ActionsProps {
  status: ZohoStatus;
  redirecting: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
}

function ZohoActions({ status, redirecting, onConnect, onDisconnect }: ActionsProps) {
  const { t } = useTranslation();
  const reconnect = status.state === "needs_reconnect";
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 pl-13">
      {status.state === "connected" ? null : (
        <Button size="sm" loading={redirecting} onClick={onConnect} data-testid={reconnect ? "zoho-reconnect" : "zoho-connect"}>
          <Icon name={reconnect ? "refresh-cw" : "log-in"} />
          {redirecting ? t(`${ZOHO_I18N}.redirecting`) : t(`${ZOHO_I18N}.${reconnect ? "reconnect" : "connect"}`)}
        </Button>
      )}
      {isLinked(status) ? (
        <Button variant="secondary" size="sm" disabled={redirecting} onClick={onDisconnect} data-testid="zoho-disconnect">
          {t("settings.integrations.disconnect")}
        </Button>
      ) : null}
    </div>
  );
}

function ZohoCardLoaded({ status, redirect }: { status: ZohoStatus; redirect: Redirect }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const client = useClient();
  const permissions = usePermissions();
  const manage = useCanManage("integrations.manage");
  const { connect } = useZohoMutations();
  const [region, setRegion] = useState("");
  const [disconnecting, setDisconnecting] = useState(false);
  useResetOnPageShow(connect.reset);
  const redirecting = connect.isPending || connect.isSuccess;
  const connectable = status.state === "disconnected" || status.state === "needs_reconnect";
  const start = () => {
    connect.mutate(chosenDatacenter(region, status.regions), {
      onSuccess: ({ authorize_url }) => {
        if (!isZohoAuthorizeUrl(authorize_url, { apiBaseUrl: client.baseUrl, pageOrigin: window.location.origin })) {
          connect.reset();
          toast.error(t(`${ZOHO_I18N}.notConfigured`));
          return;
        }
        redirect(authorize_url);
      },
      onError: (error) => { toast.error(isApiError(error) && error.status === SERVICE_UNAVAILABLE ? t(`${ZOHO_I18N}.notConfigured`) : message(error)); },
    });
  };
  return (
    <article className={cn("card card-body", isLinked(status) && "md:col-span-2")} data-testid="integration-zoho" aria-labelledby="zoho-title">
      <ZohoHeader status={status} />
      {isLinked(status) ? <ZohoDetails status={status} /> : null}
      {manage.allowed && connectable && hasRegionChoice(status.regions) ? (
        <ZohoRegionSelect regions={status.regions} value={region} onChange={setRegion} disabled={redirecting} />
      ) : null}
      {manage.allowed && status.state !== "unavailable" ? (
        <ZohoActions status={status} redirecting={redirecting} onConnect={start} onDisconnect={() => { setDisconnecting(true); }} />
      ) : null}
      {manage.allowed && status.state === "disconnected" ? (
        <p className="mt-2 pl-13 text-xs text-fg-subtle">{t(`${ZOHO_I18N}.connectNote`, { product: PRODUCT_NAME })}</p>
      ) : null}
      {!manage.allowed && !permissions.isPending && status.state !== "unavailable" ? (
        <p className="mt-3 pl-13 text-xs text-fg-muted" data-testid="zoho-readonly">{t(`${ZOHO_I18N}.readOnly`)}</p>
      ) : null}
      <ZohoDisconnectDialog open={disconnecting} onOpenChange={setDisconnecting} />
    </article>
  );
}

function ZohoCardError({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <article className="card card-body" data-testid="integration-zoho" aria-labelledby="zoho-title">
      <ZohoHeader status={null} />
      <ErrorState headingLevel={3} title={t(`${ZOHO_I18N}.loadErrorTitle`)} description={t("settings.loadErrorBody")} onRetry={onRetry} retryLabel={t("common.retry")} />
    </article>
  );
}

interface ZohoCardProps {
  /** Same-tab redirect (rule 8: no auth popups). Injected for tests. */
  redirect?: Redirect;
}

/** Zoho CRM card: unavailable, or Connect (Zoho picks the account's region) / connected details / Reconnect, then Disconnect. */
export function ZohoCard({ redirect = (url) => { window.location.assign(url); } }: ZohoCardProps) {
  const status = useZohoStatus();
  if (status.isPending) return <ZohoCardSkeleton />;
  if (status.isError) return <ZohoCardError onRetry={() => void status.refetch()} />;
  return <ZohoCardLoaded status={status.data} redirect={redirect} />;
}
